import React, { useContext } from "react";
import PropTypes from "prop-types";
import ChecklistCard from "../../../profiles/ChecklistCard/ChecklistCard";
import ConnectSection, { connectTitle } from "../../../profiles/sections/ConnectSection";
import { CompanyContext } from "../../data/company_context_management";
import PrProfileList from "./pr_profile_list";
import PrAgencyList from "./pr_agency_list";
import PeopleCard from "./people_card";
import ClaimThisPageBox from "./claim_this_page_box";

// The Checklist card's factor list for a Company profile, in the completion view's own row order —
// the 8 boolean columns id_company_completion_scores_view exposes (see the view's own scoring
// comment for the point weights behind `completion_score`).
const COMPANY_CHECKLIST_FACTORS = [
  { key: "has_profile_image", label: "Add a logo" },
  { key: "has_banner", label: "Add a cover photo" },
  { key: "has_description", label: "Add a description" },
  { key: "has_headline", label: "Add a headline" },
  { key: "has_contact_details", label: "Add contact info" },
  { key: "has_representatives", label: "Add a PR representative" },
  { key: "has_quotes", label: "Get quoted" },
  { key: "has_jobs_or_products", label: "Add your team or products" },
];

function Sidebar({
  userId = undefined,
  checklist = { score: null, isLoading: false, hide: () => {} },
  checklistContributes = false,
  contactDetails = [],
  connectActions = null,
  connectHasContent = false,
  people = { members: [], isLoading: false },
}) {
  const { company, setOpenEditModal } = useContext(CompanyContext);

  // Deep-links checklist rows to the edit modal section that actually fixes them. The remaining
  // factors stay static: `has_profile_image`/`has_banner` are images (no deep-link this round),
  // `has_contact_details` has no edit modal, and `has_representatives`/`has_quotes`/
  // `has_jobs_or_products` each span more than one routed modal key (PR_PROFILES/PR_AGENCIES/PR_TEAM
  // for representatives; EXPERTS vs. BRANDS for team-or-products) with no unambiguous single target.
  const checklistFactorActions = {
    has_description: () => setOpenEditModal("MAIN"),
    has_headline: () => setOpenEditModal("HEADER"),
    // The representatives edit window is a regular-company surface only: a PR firm's people are
    // employees (Our PR Team), so its checklist row stays a plain row there. The remaining factors
    // stay unwired because no single edit window covers them (logo/banner edit in place on the
    // header; quotes are earned, not entered).
    ...(company.displayType !== "pr_firm" && {
      has_representatives: () => setOpenEditModal("PR_PROFILES"),
    }),
  };

  return (
    <div className="h-100">
      {checklistContributes && (
        <ChecklistCard
          score={checklist.score}
          factors={COMPANY_CHECKLIST_FACTORS}
          isLoading={checklist.isLoading}
          factorActions={checklistFactorActions}
          title="Complete Profile"
          showEncouragement={false}
          onHide={checklist.hide}
        />
      )}

      {connectHasContent && (
        <ConnectSection
          id="company-rail-connect"
          title={connectTitle(company?.name)}
          contactDetails={contactDetails}
          actions={connectActions}
        />
      )}

      {!userId && <ClaimThisPageBox />}
      {company.displayType === "default" && (
        <>
          <PrProfileList />
          <PrAgencyList />
        </>
      )}

      <PeopleCard members={people.members} isLoading={people.isLoading} />
    </div>
  );
}

Sidebar.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  checklist: PropTypes.shape({
    score: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string])),
    isLoading: PropTypes.bool,
    hide: PropTypes.func,
  }),
  checklistContributes: PropTypes.bool,
  contactDetails: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string, value: PropTypes.string })),
  connectActions: PropTypes.node,
  connectHasContent: PropTypes.bool,
  people: PropTypes.shape({
    members: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        imageUrl: PropTypes.string,
        slug: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    isLoading: PropTypes.bool,
  }),
};

export default Sidebar;
